"use client";

import { TopBar } from "@package/subframe/components/TopBar";
import { useLocation } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AppSidebar } from "@/components/app-sidebar";
import { UserMenu, UserMenuTrigger } from "@/components/user-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useDeviceClass } from "@/hooks/use-device-class";
import { useServiceWorkerUpdate } from "@/hooks/use-service-worker-update";
import { userRoleTypeLabel } from "@/lib/roles";

// Routes where the layout TopBar should be hidden (full-screen experience)
const FULL_SCREEN_ROUTES = ["/checklists/"];

// Routes that should NOT be full-screen even though their prefix matches
const FULL_SCREEN_EXCLUDED_ROUTES = ["/follow-up-actions/create"];

interface ProtectedSidebarLayoutProps {
  children: React.ReactNode;
}

export function ProtectedSidebarLayout({
  children,
}: ProtectedSidebarLayoutProps) {
  const { user, displayLogin, userRoleType } = useAuth();
  const location = useLocation();

  // Check if current route should be full-screen (hide TopBar and sidebar)
  const isFullScreenRoute =
    !FULL_SCREEN_EXCLUDED_ROUTES.some((route) =>
      location.pathname.startsWith(route),
    ) &&
    [...FULL_SCREEN_ROUTES, "/follow-up-actions/"].some((route) =>
      location.pathname.startsWith(route),
    );

  // One flag for both renderings of the sidebar — the inline desktop column and
  // the phone overlay — since only ever one of them is visible at a time.
  const deviceClass = useDeviceClass();
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => deviceClass === "desktop",
  );

  // Tablets open on the work, not the menu (NAU-395). Rotating a tablet keeps
  // it a tablet, so this only re-applies on a real change of device class.
  useEffect(() => {
    setIsSidebarOpen(deviceClass === "desktop");
  }, [deviceClass]);

  // Extract stable user data from auth context
  const userData = useMemo(
    () => ({
      name:
        `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() ||
        user?.email?.split("@")[0] ||
        "User",
      email: displayLogin,
      avatar: "",
    }),
    [user?.email, user?.firstName, user?.lastName, displayLogin],
  );

  const roleLabel = userRoleTypeLabel(userRoleType);
  const displayName = user?.firstName?.trim() || userData.name;

  // Handle service worker updates (especially important for iOS PWAs)
  useServiceWorkerUpdate();

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // Full-screen mode: no sidebar, no TopBar
  if (isFullScreenRoute) {
    return (
      <div className="flex h-dvh w-full flex-col bg-default-background overflow-hidden">
        {children}
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-full overflow-hidden">
      <AppSidebar
        isMobileOpen={isSidebarOpen}
        isDesktopOpen={isSidebarOpen}
        onCloseMobile={closeMobileSidebar}
      />
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-default-background">
        <div className="flex w-full shrink-0 items-start bg-default-background">
          <TopBar
            userName={userData.name}
            userMenu={<UserMenu user={userData} />}
            rightFirst={<UserMenuTrigger user={userData} />}
            rightSecond={
              <span className="text-body font-body text-neutral-900 mobile:hidden">
                {roleLabel ? `${displayName}, ${roleLabel}` : displayName}
              </span>
            }
            onMenuClick={toggleSidebar}
          />
        </div>
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-8 mobile:p-4">
          {children}
        </main>
      </div>
    </div>
  );
}
